import { motion } from 'framer-motion';
import { WaterDroplets } from './WaveDivider';

export const PageHero = ({ 
  title, 
  titleAccent, 
  subtitle, 
  badge 
}) => {
  return (
    <section className="relative min-h-[60vh] md:min-h-[50vh] flex items-center overflow-hidden py-16 md:py-20">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E3A8A] to-[#1E40AF]">
        {/* Animated mesh gradient overlay */}
        <motion.div 
          className="absolute inset-0 opacity-30"
          style={{
            background: 'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)'
          }}
          animate={{
            background: [
              'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)',
              'radial-gradient(circle at 40% 20%, #DC2626 0%, transparent 50%), radial-gradient(circle at 60% 80%, #3B82F6 0%, transparent 50%)',
              'radial-gradient(circle at 80% 50%, #DC2626 0%, transparent 50%), radial-gradient(circle at 20% 50%, #3B82F6 0%, transparent 50%)',
              'radial-gradient(circle at 20% 80%, #DC2626 0%, transparent 50%), radial-gradient(circle at 80% 20%, #3B82F6 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      {/* Water droplets */}
      <WaterDroplets count={15} />

      {/* Floating shapes */}
      <motion.div 
        className="absolute top-20 left-10 w-32 h-32 rounded-full bg-red-500/20 blur-3xl"
        animate={{ 
          x: [0, 30, 0], 
          y: [0, -20, 0],
          scale: [1, 1.2, 1]
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div 
        className="absolute bottom-20 right-10 w-48 h-48 rounded-full bg-blue-400/20 blur-3xl"
        animate={{ 
          x: [0, -40, 0], 
          y: [0, 30, 0],
          scale: [1, 1.3, 1]
        }}
        transition={{ duration: 10, repeat: Infinity, ease: "easeInOut", delay: 2 }}
      />

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10 w-full">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-4xl mx-auto"
        >
          {badge && (
            <motion.span 
              className="inline-block text-red-500 font-bold tracking-[0.2em] uppercase text-sm mb-4"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              {badge}
            </motion.span>
          )}
          
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.9] mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            {titleAccent ? (
              <>
                <span className="text-white">{title}</span>
                <br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-red-400">
                  {titleAccent}
                </span>
              </>
            ) : (
              <span className="text-white">{title}</span>
            )}
          </motion.h1>
          
          {subtitle && (
            <motion.p 
              className="text-lg md:text-xl text-blue-100/90 leading-relaxed max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              {subtitle}
            </motion.p>
          )}
        </motion.div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg 
          className="w-full h-16 md:h-24"
          viewBox="0 0 1440 100" 
          preserveAspectRatio="none"
          fill="#FFFFFF"
        >
          <motion.path 
            d="M0,100 L0,40 Q180,80 360,40 T720,40 T1080,40 T1440,40 L1440,100 Z"
            animate={{
              d: [
                "M0,100 L0,40 Q180,80 360,40 T720,40 T1080,40 T1440,40 L1440,100 Z",
                "M0,100 L0,60 Q180,20 360,60 T720,60 T1080,60 T1440,60 L1440,100 Z",
                "M0,100 L0,40 Q180,80 360,40 T720,40 T1080,40 T1440,40 L1440,100 Z"
              ]
            }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </svg>
      </div>
    </section>
  );
};

export default PageHero;
